import React, { useEffect, useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { useToast } from '@/hooks/useToast';
import { createApiService } from '@/services/api';
import {
  BarChart,
  Bar,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Brain, DollarSign, MessageSquare, RefreshCw } from 'lucide-react';
import { BrandedLoading } from '@/components/common/BrandedLoading';
import { Button } from '@/components/ui/Button';

const analyticsApi = createApiService(
  import.meta.env.VITE_ANALYTICS_API_BASE_URL || '/api/analytics'
);

interface CostByModel {
  model: string;
  cost: number;
}

interface CostPerUser {
  email: string;
  cost: number;
}

interface DailyCost {
  date: string;
  cost: number;
}

interface LLMCostState {
  totalTokenSpend: number;
  costByModel: CostByModel[];
  costPerUser: CostPerUser[];
  dailyCostTrend: DailyCost[];
  avgCostPerConversation: number;
  totalConversations: number;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(value);
}

export const LLMCostReportPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<LLMCostState>({
    totalTokenSpend: 0,
    costByModel: [],
    costPerUser: [],
    dailyCostTrend: [],
    avgCostPerConversation: 0,
    totalConversations: 0,
  });
  const { toast } = useToast();

  const fetchData = async () => {
    try {
      setIsLoading(true);

      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const startDate = thirtyDaysAgo.toISOString().split('T')[0] + 'T00:00:00';
      const endDate = now.toISOString().split('T')[0] + 'T00:00:00';

      const result = await analyticsApi
        .get<Record<string, unknown>>(
          `/api/v1/usage-metrics/llm/summary?startDate=${startDate}&endDate=${endDate}`
        )
        .catch(() => ({}));

      const obj = (result ?? {}) as Record<string, unknown>;

      const totalTokenSpend = Number(obj.totalCost ?? obj.totalTokenSpend ?? 0);
      const totalConversations = Number(obj.totalConversations ?? 0);
      const avgCostPerConversation =
        totalConversations > 0 ? totalTokenSpend / totalConversations : 0;

      // Cost by model
      const costByModel = ((obj.costByModel ?? []) as CostByModel[]).sort(
        (a, b) => b.cost - a.cost
      );

      // Cost per user (top 10)
      const costPerUser = ((obj.costPerUser ?? obj.topUsers ?? []) as CostPerUser[])
        .sort((a, b) => b.cost - a.cost)
        .slice(0, 10);

      // Daily cost trend
      const dailyCostTrend = ((obj.dailyCostTrend ?? obj.dailyCosts ?? []) as DailyCost[]).map(
        (d) => ({
          date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          cost: d.cost,
        })
      );

      setData({
        totalTokenSpend,
        costByModel,
        costPerUser,
        dailyCostTrend,
        avgCostPerConversation,
        totalConversations,
      });
    } catch (error) {
      console.error('Error fetching LLM cost data:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load LLM cost report',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const MODEL_COLORS: Record<string, string> = {
    'gpt-4o-mini': '#10b981',
    'gpt-4o': '#6366f1',
    'o3-mini': '#f59e0b',
  };

  const kpiCards = useMemo(
    () => [
      {
        title: 'Total Token Spend',
        value: formatCurrency(data.totalTokenSpend),
        description: 'This month',
        icon: <DollarSign className="h-4 w-4 text-green-600" />,
        color: 'text-green-600',
      },
      {
        title: 'Total Conversations',
        value: data.totalConversations.toLocaleString(),
        description: 'Last 30 days',
        icon: <MessageSquare className="h-4 w-4 text-blue-600" />,
        color: 'text-blue-600',
      },
      {
        title: 'Avg Cost / Conversation',
        value: formatCurrency(data.avgCostPerConversation),
        description: 'Total cost / Total conversations',
        icon: <Brain className="h-4 w-4 text-purple-600" />,
        color: 'text-purple-600',
      },
    ],
    [data]
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Brain className="h-8 w-8" />
            LLM Cost Report
          </h1>
          <p className="text-muted-foreground mt-2">
            Token usage, model costs, and spend analytics
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} disabled={isLoading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <BrandedLoading message="Loading LLM cost data…" iconSize="sm" />
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid gap-4 md:grid-cols-3">
            {kpiCards.map((kpi) => (
              <Card key={kpi.title}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">{kpi.title}</CardTitle>
                  {kpi.icon}
                </CardHeader>
                <CardContent>
                  <div className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</div>
                  <p className="text-xs text-muted-foreground mt-1">{kpi.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Cost by Model */}
          <Card>
            <CardHeader>
              <CardTitle>Cost by Model</CardTitle>
              <CardDescription>Spend breakdown by LLM model</CardDescription>
            </CardHeader>
            <CardContent>
              {data.costByModel.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={data.costByModel}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="model" />
                    <YAxis tickFormatter={(v) => `$${v}`} />
                    <Tooltip formatter={(value: number) => formatCurrency(value)} />
                    <Legend />
                    <Bar dataKey="cost" name="Cost" radius={[4, 4, 0, 0]}>
                      {data.costByModel.map((entry) => (
                        <Cell
                          key={entry.model}
                          fill={MODEL_COLORS[entry.model] || '#6b7280'}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No model cost data available. Data will appear here once the analytics service reports LLM usage.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Daily Cost Trend */}
          <Card>
            <CardHeader>
              <CardTitle>Daily Cost Trend</CardTitle>
              <CardDescription>LLM spend over the last 30 days</CardDescription>
            </CardHeader>
            <CardContent>
              {data.dailyCostTrend.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={data.dailyCostTrend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis tickFormatter={(v) => `$${v}`} />
                    <Tooltip formatter={(value: number) => formatCurrency(value)} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="cost"
                      stroke="#6366f1"
                      strokeWidth={2}
                      name="Daily Cost"
                      dot={{ r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No daily cost trend data available
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top 10 Users by Cost */}
          <Card>
            <CardHeader>
              <CardTitle>Top 10 Users by LLM Cost</CardTitle>
              <CardDescription>Highest consumers of LLM tokens</CardDescription>
            </CardHeader>
            <CardContent>
              {data.costPerUser.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-3 font-medium">#</th>
                        <th className="text-left py-2 px-3 font-medium">User</th>
                        <th className="text-right py-2 px-3 font-medium">Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.costPerUser.map((user, idx) => (
                        <tr key={user.email} className="border-b hover:bg-muted/50">
                          <td className="py-2 px-3 text-muted-foreground">{idx + 1}</td>
                          <td className="py-2 px-3">{user.email}</td>
                          <td className="py-2 px-3 text-right font-mono">
                            {formatCurrency(user.cost)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No per-user cost data available
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default LLMCostReportPage;
